import React, { Fragment, FC, useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { TrackingProp } from 'react-tracking';
import { raygun } from '@ynap/clientside-monitoring';
import BEMHelper from '@ynap/bem-helper';
import { Field as FormField, usePrevious, useFormField, FormValues, FieldRenderProps } from '@ynap/form';
import { Button } from '@ynap/button';
import { AddressValidationOptions, Field, Countries, MappedSelectedAddressField, FieldWidthType, Messages, Config, CheckoutAnalytics } from './@types';
import { AddressField } from './AddressField';
import { toWCSMapping, setAddressFinderField, useAddressValidateContext, addressValidation, findValue } from './utils';
import { CountrySelectField } from './CountrySelectField';

const bem = new BEMHelper('AddressForm');

export type AddressFieldsProps = {
    config: Config;
    country: string;
    hideCountry?: boolean;
    initialValues?: FormValues | { [key: string]: string };
    messages: Messages;
    tracking?: TrackingProp;
    addressValidationOptions: AddressValidationOptions;
    setFormTouched?: (touched: boolean) => void;
    isCheckoutLayout?: boolean;
    enableLoqateOn?: boolean;
    setRemoveAddressFinder?: Dispatch<SetStateAction<() => any>>;
    optimizely?: {
        track: (arg1: string) => void;
    };
    primaryCategory?: string;
    checkoutAnalytics?: CheckoutAnalytics;
    reduxForm?: any;
    invalidFields?: string[];
};

const fieldWidth: FieldWidthType = {
    COUNTRY: 'large',
    PERSON_TITLE: 'small',
    FIRST_NAME: 'medium',
    LAST_NAME: 'medium',
    CITY: 'large',
    PROVINCE: 'large',
    ZIP: 'medium',
    PHONE: 'large',
    ALTERNATE_PHONE: 'large',
    PCCC: 'large',
};

export const AddressFields: FC<AddressFieldsProps> = ({
    config,
    country: initialCountry,
    hideCountry = false,
    initialValues,
    messages,
    tracking,
    addressValidationOptions,
    setFormTouched,
    isCheckoutLayout,
    setRemoveAddressFinder,
    enableLoqateOn,
    optimizely,
    primaryCategory,
    checkoutAnalytics,
    reduxForm,
    invalidFields
}) => {
    const { countries, setCountries, countryList } = useAddressValidateContext();
    const [{ value: formCountry }] = useFormField('country');
    const [stateField, setStateField] = useFormField('state');
    const [postCodeField, setPostCodeField] = useFormField('zipCode');
    const country = formCountry || initialCountry;
    const addressFinderEnabled = config.head.loqate?.enabled && enableLoqateOn;
    const addressFinder = addressFinderEnabled ? config.head.loqate : undefined;
    const addressFields = ['addressLine[0]', 'addressLine[1]', 'city', 'state', 'zipCode'];
    const [fieldsWCS, setFieldsWCS] = useState(getFields(countries, country, addressFinderEnabled));
    const [fetchingCountries, setFetchingCountry] = useState<Array<string>>([]);
    const [selectedAddress, setSelectedAddress] = useState<Array<MappedSelectedAddressField>>([]);
    const [editingAddress, setEditingAddress] = useState(!!initialValues?.addressLine);
    const [secondAddressLineVisible, setSecondAddressLineVisible] = useState(!isCheckoutLayout);
    const [alternatePhoneVisible, setAlternatePhoneVisible] = useState(!isCheckoutLayout);
    const [countryChanged, setCountryChanged] = useState(false);
    const addressFieldsMaxChars = fieldsWCS
        .filter((field) => ['ADDRESS_LINE_1', 'ADDRESS_LINE_2'].includes(field.type))
        .map(({ maxLength }) => maxLength || 100);
    const prevCountry = usePrevious(country);
    const prevState = usePrevious(stateField);
    const addressFinderVisible = fieldsWCS.filter((field) => String(field.type) === 'ADDRESS_FINDER').length > 0;

    useEffect(() => {
        setFieldsWCS(getFields(countries, country, addressFinderEnabled));
    }, [addressFinderEnabled]);

    // edge case: we force the address fields to open completely to show the user incorrect fields 
    useEffect(() => {
        if (invalidFields?.length) {
            setFieldsWCS(getFields(countries, country, false))
        }
    }, [ invalidFields ])

    useEffect(() => {
        if (typeof countries[country] == 'undefined' && !fetchingCountries.includes(country)) {
            setFetchingCountry([...fetchingCountries, country]);
            addressValidation({ countryCode: country, config, countries: countryList })
                .then((newCountries) => {
                    setCountries({ ...countries, ...newCountries });
                    setFieldsWCS(getFields(newCountries, country, addressFinderEnabled));
                    setFetchingCountry(fetchingCountries.filter((fetchingCountry) => fetchingCountry != country));
                })
                .catch(() => {
                    setFetchingCountry(fetchingCountries.filter((fetchingCountry) => fetchingCountry != country));
                });
        }

        if (typeof prevCountry != 'undefined' && prevCountry != country) {
            setStateField({ ...stateField, value: '', errors: [], touched: undefined });
            setFormTouched?.(false);
            setCountryChanged(true);
            if (typeof countries[country] != 'undefined') setFieldsWCS(getFields(countries, country, addressFinderEnabled));
        }

        if (!addressFinderVisible && typeof prevState != 'undefined' && prevState?.value != stateField?.value) {
            setPostCodeField({ ...postCodeField, errors: [], touched: undefined });
            setFormTouched?.(false);
        }

        if (Array.isArray(initialValues?.addressLine) && !!initialValues?.addressLine[1]) {
            setSecondAddressLineVisible(true);
        }

        if (initialValues?.phone2) {
            setAlternatePhoneVisible(true);
        }

        if (addressFinderEnabled) {
            if (addressFinderVisible && editingAddress) {
                handleRemoveAddressFinder();
                setEditingAddress(false);
            }

            const foundSecondAddressLine = selectedAddress.find((address: MappedSelectedAddressField) => address.field === 'addressLine[1]')?.name;
            if (typeof foundSecondAddressLine != 'undefined' && foundSecondAddressLine !== '') {
                setSecondAddressLineVisible(true);
            }
        }
    }, [country, fetchingCountries, stateField, fieldsWCS, selectedAddress, initialValues]);

    useEffect(() => {
        if (typeof countries[country] !== 'undefined' && !fetchingCountries.includes(country)) {
            setRemoveAddressFinder?.(() => () => setFieldsWCS(getFields(countries, country, false)));
        }
    }, [country, fetchingCountries]);

    useEffect(() => {
        // Send discrepancies between Address Validation and Loqate to the Raygun to identify opportunities for Address Validation improvements
        selectedAddress.length > 0 &&
            selectedAddress.forEach((selectedField) => {
                const { field, name } = selectedField;
                const foundField = fieldsWCS?.find((fieldWCS: Field) => fieldWCS.wcsType?.name === field);
                const regex = foundField?.regex && new RegExp(foundField.regex);
                const notInList = !!foundField?.values && !findValue(foundField, selectedField);
                const invalid = regex && !name.match(regex);

                if (invalid || notInList) {
                    (invalid || foundField?.mandatory) && optimizely?.track('address_errors_web00008');
                    raygun.helpers.agent('send', {
                        error: new Error(`Selected address field from loqate ${notInList ? "was not found in WAPI's list" : 'did not pass WAPI validation'}`),
                        tags: ['loqate'],
                        customData: {
                            country,
                            field,
                            value: name,
                        },
                    });
                }
            });
    }, [selectedAddress]);

    const countryField: Field = {
        wcsType: { name: 'country' },
        type: 'COUNTRY',
        mandatory: true,
        minLength: 0,
        maxLength: 2,
        values: countryList.map(({ countryCode: value, countryName: display }) => ({
            display,
            value,
        })),
    };

    const shouldValidateNonLatin = !addressValidationOptions?.nonLatinValidationBlocklist?.includes(country);

    const addressType = initialValues && initialValues.addressType ? initialValues.addressType : 'ShippingAndBilling';

    const handleAddNewAddressLine = () => {
        setSecondAddressLineVisible(true);
    };

    const handleAddAlternatePhone = () => {
        setAlternatePhoneVisible(true);
    };

    const handleRemoveAddressFinder = () => {
        setFieldsWCS(fieldsWCS.filter((field) => String(field.type) !== 'ADDRESS_FINDER'));
    };

    const handleSelectedAddressClick = (address: MappedSelectedAddressField[]) => {
        handleRemoveAddressFinder();
        setSelectedAddress(address);
    };

    const handleAddAddressFinder = () => {
        setFieldsWCS(getFields(countries, country, addressFinderEnabled));
        setSelectedAddress([]);
    };

    const formFields = useMemo(() => {
        if (!config.head.fieldsOrdered) return [];
        let fields: Field[] = fieldsWCS.map((field) => ({ ...field, width: fieldWidth[field.type] }));

        // add country after lastName (if missing)
        if (!fields.find((it: Field) => it.type === 'COUNTRY')) {
            const lastNameIndex = fields.findIndex((it: Field) => it.type === 'LAST_NAME');
            const firstNameIndex = fields.findIndex((it: Field) => it.type === 'FIRST_NAME');
            const countryIndex = Math.max(lastNameIndex, firstNameIndex) + 1;
            if (lastNameIndex !== -1 || firstNameIndex !== -1)
                fields.splice(countryIndex, 0, { ...countryField, width: hideCountry ? 'hidden' : fieldWidth.COUNTRY });
        }
        // add addressFinder after last address field (if enabled and missing)
        if (!fields.find((it: Field) => it.type === 'ADDRESS_FINDER') && addressFinderEnabled) {
            fields = injectAddressFinder(fields);
        }
        return fields;
    }, [fieldsWCS]);

    const useFormFields = formFields.length > 0;
    // If onSubmit is provided, render a complete form. Otherwise just return the fields.
    return useMemo(() => {
        return (
            <Fragment>
                {!useFormFields &&
                    (!hideCountry ? (
                        <CountrySelectField
                            className={bem('country')}
                            key="country"
                            innerKey="country"
                            messages={messages}
                            field={formFields.find((field) => String(field.type).toLowerCase() === 'country') || countryField}
                            initialValue={country}
                        />
                    ) : (
                        <FormField name="country" value={country}>
                            {({ name, value }: FieldRenderProps) => <input type="hidden" name={name} value={value} />}
                        </FormField>
                    ))}
                <FormField name="addressType" value={String(addressType)}>
                    {({ name, value }: FieldRenderProps) => <input type="hidden" name={name} value={value} />}
                </FormField>

                <div className={useFormFields ? bem('dynamicForm') : ''}>
                    {(useFormFields ? formFields : fieldsWCS).map((field) => {
                        const { name, accessor } = field.wcsType || {};
                        const selectedField = selectedAddress?.find((address: MappedSelectedAddressField) => address.field == name);
                        const foundValue = selectedField && findValue(field, selectedField);

                        const hideFields =
                            (selectedAddress &&
                                isCheckoutLayout &&
                                ((!secondAddressLineVisible && name === 'addressLine[1]') ||
                                    (!alternatePhoneVisible && name === 'phone2') ||
                                    (!addressFinderVisible && name === 'Address Finder'))) ||
                            (addressFinderVisible && name && addressFields.includes(name));

                        let initialValue;
                        if (accessor) {
                            initialValue = accessor(initialValues);
                        } else if (initialValues && name && initialValues[name]) {
                            initialValue = initialValues[name];
                        }
                        const addressFinderParams = addressFinder?.enabled
                            ? { addressFinder, handleRemoveAddressFinder, handleSelectedAddressClick, addressFieldsMaxChars }
                            : null;

                        // As we need to target this className in CSS, we needed to remove [] as this is not accepted in CSS
                        const parsedName = name?.replace(/[[\]]/g, '') || undefined;
                        // useWcsField showAll
                        return (
                            <div key={`field-${name}`} className={useFormFields ? bem(parsedName, field.width) : ''}>
                                {!useFormFields ? (
                                    <AddressField
                                        className={bem(parsedName, isCheckoutLayout ? { isCheckoutLayout } : undefined)}
                                        key={name}
                                        innerKey={name || ''}
                                        country={country}
                                        messages={messages}
                                        field={field}
                                        initialValue={!countryChanged || name === 'addressType' ? initialValue : ''}
                                        tracking={tracking}
                                        shouldValidateNonLatin={shouldValidateNonLatin}
                                        addressFinderParams={addressFinderParams}
                                        foundValue={foundValue}
                                        isCheckoutLayout={isCheckoutLayout}
                                        primaryCategory={primaryCategory}
                                        checkoutAnalytics={checkoutAnalytics}
                                        reduxForm={reduxForm}
                                    />
                                ) : hideCountry && field.type === 'COUNTRY' ? (
                                    <FormField name="country" value={country}>
                                        {({ name, value }: FieldRenderProps) => <input type="hidden" name={name} value={value} />}
                                    </FormField>
                                ) : (
                                    <>
                                        {field.type === 'COUNTRY' && !hideCountry && (
                                            <CountrySelectField
                                                className={bem('country')}
                                                key="country"
                                                innerKey="country"
                                                messages={messages}
                                                field={fieldsWCS.find((field) => String(field.type).toLowerCase() === 'country') || countryField}
                                                initialValue={country}
                                            />
                                        )}
                                        {!['COUNTRY'].includes(field.type) && !hideFields && (
                                            <AddressField
                                                className={bem(parsedName, isCheckoutLayout ? { isCheckoutLayout } : undefined)}
                                                key={name}
                                                innerKey={name || ''}
                                                country={country}
                                                messages={messages}
                                                field={field}
                                                initialValue={!countryChanged || name === 'addressType' ? initialValue : ''}
                                                tracking={tracking}
                                                shouldValidateNonLatin={shouldValidateNonLatin}
                                                addressFinderParams={addressFinderParams}
                                                foundValue={foundValue}
                                                isCheckoutLayout={isCheckoutLayout}
                                                primaryCategory={primaryCategory}
                                                checkoutAnalytics={checkoutAnalytics}
                                                reduxForm={reduxForm}
                                                invalidFields={invalidFields}
                                            />
                                        )}
                                    </>
                                )}

                                {field.type === 'ADDRESS_LINE_2' && isCheckoutLayout && !secondAddressLineVisible && !addressFinderVisible ? (
                                    <Button onClick={handleAddNewAddressLine} type="primary-link" className={bem('addLineButton')} component="button">
                                        {messages.addressForm?.display?.addNewLine?.()}
                                    </Button>
                                ) : null}
                                {field.type === 'ALTERNATE_PHONE' && isCheckoutLayout && !alternatePhoneVisible ? (
                                    <Button onClick={handleAddAlternatePhone} type="primary-link" className={bem('addAlternatePhoneButton')} component="button">
                                        {messages.addressForm?.display?.addAlternatePhone?.()}
                                    </Button>
                                ) : null}
                                {field.type === 'ADDRESS_FINDER' && isCheckoutLayout && addressFinder && !addressFinderVisible ? (
                                    <Button onClick={handleAddAddressFinder} type="primary-link" className={bem('searchAddressButton')} component="button">
                                        {messages.addressForm?.addressFinder?.searchAnother?.()}
                                    </Button>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            </Fragment>
        );
    }, [country, hideCountry, fieldsWCS, messages, countryField, formFields]);
};

export const getFields = (countries: Countries, country: string, addressFinderEnabled = false) => {
    let { fields } = countries[country] || { fields: [] };
    if (!fields.find((field: Field) => String(field.type) === 'ADDRESS_FINDER') && addressFinderEnabled) {
        fields = injectAddressFinder(fields);
    }

    return fields
        .filter((field: Field) => String(field.type).toLowerCase() !== 'country')
        .map((field: Field) => ({ ...field, wcsType: toWCSMapping(field.type) }));
};

export const injectAddressFinder = (fields: Field[]) => {
    const fieldTypes = fields.map((item) => item.type);
    // ME countries do not have ZIP so CITY is the last address field
    const indexOfLastAddressField = fieldTypes.includes('ZIP') ? fieldTypes.indexOf('ZIP') : fieldTypes.indexOf('CITY');
    return [...fields.slice(0, indexOfLastAddressField + 1), setAddressFinderField(), ...fields.slice(indexOfLastAddressField + 1, fields.length)];
};
